import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { message } from '@/ui/compat/antd'
import HotelClientApi from '../../../../../api/HotelClientApi'
import SearchableSelect from '../shared/tw/SearchableSelect'
import { inputClass, labelClass, textareaClass } from '../shared'
import {
  buildBillingClientPool,
  mapHotelClientToTransfertBillingDraft,
} from '../shared/hotelBillingClientList'
import CountryPhoneInput from '../shared/CountryPhoneInput'
import {
  createBillingClientFromDraft,
  findBillingClientByName,
} from './transferBillingClientCreate'

const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}')
  } catch {
    return {}
  }
}

const userRoleForFilter = (u) => {
  if (u.role === 'Super Admin' || u.superAdmin === 1) return 'admin'
  return u.role || 'admin'
}

const emptyBillingFields = () => ({
  billing_client_id: null,
  billing_partner_id: null,
  billing_nom: '',
  billing_prenom: '',
  billing_email: '',
  billing_telephone: '',
  billing_adresse_facturation: '',
  billing_matricule_fiscal: '',
  phone_country_code: '+216',
  client_name: '',
  client_email: '',
  client_phone: '',
})

const FieldErr = ({ tamarra, children }) => {
  if (!children) return null
  if (tamarra) {
    return (
      <small className="tamarra-ferr" role="alert">
        {children}
      </small>
    )
  }
  return <p className="mt-1 text-sm text-[var(--destructive)]">{children}</p>
}

/**
 * Bloc facturation aligné sur le step 1 du modal moteur réservation hôtel (type + liste client + champs).
 */
export default function TransferBillingClientsSection({
  draft,
  setField,
  formErrors,
  appearance = 'default',
}) {
  const { t } = useTranslation()
  const tamarra = appearance === 'tamarra'
  const [clientPool, setClientPool] = useState([])
  const [isCreatingClient, setIsCreatingClient] = useState(false)

  const loadClientPool = useCallback(async (kind) => {
    if (!kind) {
      setClientPool([])
      return
    }
    const results = await HotelClientApi.getAllClientsForDropdown()
    const u = readUser()
    const role = userRoleForFilter(u)
    setClientPool(buildBillingClientPool(results, role, kind, u))
  }, [])

  useEffect(() => {
    let cancelled = false
    if (!draft.billing_client_kind) {
      setClientPool([])
      return undefined
    }
    loadClientPool(draft.billing_client_kind).catch(() => {
      if (!cancelled) setClientPool([])
    })
    return () => {
      cancelled = true
    }
  }, [draft.billing_client_kind, loadClientPool])

  const clientOptions = useMemo(
    () =>
      clientPool.map((c) => ({
        value: Number(c.id),
        label: `${c.nom || c.name || ''} ${c.prenom || ''}`.trim() || `#${c.id}`,
      })),
    [clientPool],
  )

  const resetBillingFields = () => {
    const e = emptyBillingFields()
    Object.entries(e).forEach(([k, v]) => setField(k, v))
  }

  const onKindChange = (kind) => {
    setField('billing_client_kind', kind)
    resetBillingFields()
    setField('payment_methods', {})
    setField('payment_bundle', null)
    if (kind === 'physique') setField('client_type', 'b2c')
    if (kind === 'morale') setField('client_type', 'b2b')
  }

  const onBillingClientSelect = (clientId) => {
    if (clientId == null || clientId === '') {
      setField('billing_client_id', null)
      setField('billing_partner_id', null)
      return
    }
    const client = clientPool.find((c) => Number(c.id) === Number(clientId))
    if (!client) return
    const mapped = mapHotelClientToTransfertBillingDraft(client)
    if (!mapped) return
    Object.entries(mapped).forEach(([k, v]) => setField(k, v))
    setField('payment_methods', {})
    setField('payment_bundle', null)
  }

  const applyCreatedClient = useCallback(
    (client) => {
      if (!client?.id) return
      setClientPool((prev) => {
        if (prev.some((c) => Number(c.id) === Number(client.id))) return prev
        return [...prev, client]
      })
      const mapped = mapHotelClientToTransfertBillingDraft(client)
      if (!mapped) return
      Object.entries(mapped).forEach(([k, v]) => setField(k, v))
      setField('payment_methods', {})
      setField('payment_bundle', null)
    },
    [setField],
  )

  const handleCreateBillingClient = useCallback(
    async (nomOverride) => {
      const kind = draft.billing_client_kind
      if (!kind) {
        message.error(t('transferts.reservations.billing.selectClientTypeFirst'))
        return
      }

      const nom = String(nomOverride ?? draft.billing_nom ?? '').trim()
      if (!nom) {
        message.error(t('transferts.reservations.billing.nameRequired'))
        return
      }

      const existing = findBillingClientByName(clientPool, nom)
      if (existing) {
        applyCreatedClient(existing)
        message.info(t('transferts.reservations.billing.clientExists'))
        return
      }

      setIsCreatingClient(true)
      try {
        const user = readUser()
        const newClient = await createBillingClientFromDraft(draft, kind, user, nom, t)
        applyCreatedClient(newClient)
        message.success(t('transferts.reservations.billing.clientCreated'))
      } catch (error) {
        const msg =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          t('transferts.common.unknownError')
        message.error(t('transferts.reservations.billing.createError', { error: msg }))
      } finally {
        setIsCreatingClient(false)
      }
    },
    [applyCreatedClient, clientPool, draft, t],
  )

  const canCreateClient =
    Boolean(draft.billing_client_kind) &&
    !draft.billing_client_id &&
    Boolean(String(draft.billing_nom || '').trim())

  const isPhysique = draft.billing_client_kind === 'physique'
  const isMorale = draft.billing_client_kind === 'morale'
  const pf = tamarra ? 'tamarra-pf' : null
  const inp = tamarra ? 'tamarra-pay-input' : inputClass
  const area = tamarra ? 'tamarra-pay-input tamarra-billing-area' : textareaClass

  return (
    <div
      className={
        tamarra
          ? 'tamarra-billing'
          : 'space-y-4 rounded-[var(--radius-md)] border border-[var(--border)] bg-[color-mix(in_oklab,var(--card)_80%,transparent)] p-4 shadow-sm'
      }
    >
      <h4 className={tamarra ? 'tamarra-grp' : 'text-sm font-semibold text-[var(--foreground)]'}>
        {t('transferts.reservations.billing.billingInfo')}
      </h4>

      <div className={tamarra ? 'tamarra-billing-block' : undefined}>
        {tamarra ? (
          <span className="tamarra-label">
            {t('transferts.reservations.billing.clientType')}
          </span>
        ) : (
          <span className={labelClass}>
            {t('transferts.reservations.billing.clientType')}{' '}
            <span className="text-red-500">*</span>
          </span>
        )}

        {tamarra ? (
          <div
            className="tamarra-choices"
            role="radiogroup"
            aria-label={t('transferts.reservations.billing.clientType')}
          >
            <label
              className={`tamarra-choice tamarra-choice-compact${isPhysique ? ' on' : ''}`}
            >
              <input
                type="radio"
                name="billing_client_kind_tamarra"
                value="physique"
                checked={isPhysique}
                onChange={() => onKindChange('physique')}
              />
              <b>{t('transferts.reservations.billing.physique')}</b>
            </label>
            <label
              className={`tamarra-choice tamarra-choice-compact${isMorale ? ' on' : ''}`}
            >
              <input
                type="radio"
                name="billing_client_kind_tamarra"
                value="morale"
                checked={isMorale}
                onChange={() => onKindChange('morale')}
              />
              <b>{t('transferts.reservations.billing.morale')}</b>
            </label>
          </div>
        ) : (
          <div className="mt-2 flex flex-wrap gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-[var(--foreground)]">
              <input
                type="radio"
                name="billing_client_kind_tw"
                value="physique"
                checked={isPhysique}
                onChange={() => onKindChange('physique')}
              />
              {t('transferts.reservations.billing.physique')}
            </label>
            <label className="flex cursor-pointer items-center gap-2 text-sm text-[var(--foreground)]">
              <input
                type="radio"
                name="billing_client_kind_tw"
                value="morale"
                checked={isMorale}
                onChange={() => onKindChange('morale')}
              />
              {t('transferts.reservations.billing.morale')}
            </label>
          </div>
        )}

        {!tamarra ? (
          <p className="mt-1 text-xs text-[var(--muted-foreground)]">
            {t('transferts.reservations.billing.typeHint')}
          </p>
        ) : null}
        <FieldErr tamarra={tamarra}>{formErrors.billing_client_kind}</FieldErr>
      </div>

      {draft.billing_client_kind ? (
        <>
          <div className={tamarra ? 'tamarra-billing-block' : undefined}>
            {tamarra ? (
              <label className={pf}>
                <span>
                  {t('transferts.reservations.billing.selectClient')}
                </span>
              </label>
            ) : (
              <span className={labelClass}>
                {t('transferts.reservations.billing.selectClient')}{' '}
                <span className="text-red-500">*</span>
              </span>
            )}
            <SearchableSelect
              className={tamarra ? 'tamarra-billing-select' : undefined}
              options={clientOptions}
              value={draft.billing_client_id}
              onChange={onBillingClientSelect}
              placeholder={t('transferts.reservations.billing.selectClientPlaceholder')}
              creatable
              isDisabled={isCreatingClient}
              formatCreateLabel={(inputValue) => {
                const label = String(inputValue || '').trim()
                return label
                  ? t('transferts.reservations.billing.addQuoted', { label })
                  : t('transferts.reservations.billing.addEllipsis')
              }}
              onCreateOption={(inputValue) => {
                const label = String(inputValue || '').trim()
                if (!label) return
                setField('billing_nom', label)
                setField(
                  'client_name',
                  [label, draft.billing_prenom].filter(Boolean).join(' ').trim(),
                )
                handleCreateBillingClient(label)
              }}
            />
            <p className={tamarra ? 'tamarra-bk-rate' : 'mt-1 text-xs text-[var(--muted-foreground)]'}>
              {t('transferts.reservations.billing.listHint')}
            </p>
            <FieldErr tamarra={tamarra}>{formErrors.billing_client_id}</FieldErr>
          </div>

          {isPhysique ? (
            <div className={tamarra ? 'tamarra-billing-g' : 'grid grid-cols-1 gap-3 md:grid-cols-3'}>
              <label className={pf || undefined} htmlFor="billing_nom">
                {tamarra ? (
                  <span>{t('transferts.reservations.billing.lastName')}</span>
                ) : (
                  <span className={labelClass}>
                    {t('transferts.reservations.billing.lastName')}{' '}
                    <span className="text-red-500">*</span>
                  </span>
                )}
                <input
                  id="billing_nom"
                  className={inp}
                  value={draft.billing_nom}
                  aria-invalid={!!formErrors.billing_nom}
                  onChange={(e) => {
                    setField('billing_nom', e.target.value)
                    setField(
                      'client_name',
                      [e.target.value, draft.billing_prenom].filter(Boolean).join(' ').trim(),
                    )
                  }}
                  placeholder={t('transferts.reservations.billing.lastNamePlaceholder')}
                />
                <FieldErr tamarra={tamarra}>{formErrors.billing_nom}</FieldErr>
              </label>

              <label className={pf || undefined} htmlFor="billing_prenom">
                {tamarra ? (
                  <span>{t('transferts.reservations.billing.firstName')}</span>
                ) : (
                  <span className={labelClass}>
                    {t('transferts.reservations.billing.firstName')}
                  </span>
                )}
                <input
                  id="billing_prenom"
                  className={inp}
                  value={draft.billing_prenom}
                  onChange={(e) => {
                    setField('billing_prenom', e.target.value)
                    setField(
                      'client_name',
                      [draft.billing_nom, e.target.value].filter(Boolean).join(' ').trim(),
                    )
                  }}
                  placeholder={t('transferts.reservations.billing.firstNamePlaceholder')}
                />
              </label>

              <div className={tamarra ? 'tamarra-billing-phone' : undefined}>
                <CountryPhoneInput
                  id="billing_telephone"
                  countryCode={draft.phone_country_code || '+216'}
                  phone={draft.billing_telephone}
                  onCountryCodeChange={(v) => setField('phone_country_code', v)}
                  onPhoneChange={(v) => {
                    setField('billing_telephone', v)
                    setField('client_phone', v)
                  }}
                />
              </div>

              <label className={pf || undefined} htmlFor="billing_email">
                {tamarra ? (
                  <span>{t('transferts.common.email')}</span>
                ) : (
                  <span className={labelClass}>{t('transferts.common.email')}</span>
                )}
                <input
                  id="billing_email"
                  type="email"
                  className={inp}
                  value={draft.billing_email}
                  onChange={(e) => {
                    setField('billing_email', e.target.value)
                    setField('client_email', e.target.value)
                  }}
                />
              </label>
            </div>
          ) : (
            <div className={tamarra ? 'tamarra-billing-g' : 'grid grid-cols-1 gap-3 md:grid-cols-2'}>
              <label
                className={tamarra ? `${pf} tamarra-billing-wide` : 'md:col-span-2'}
                htmlFor="billing_nom_m"
              >
                {tamarra ? (
                  <span>{t('transferts.reservations.billing.companyName')}</span>
                ) : (
                  <span className={labelClass}>
                    {t('transferts.reservations.billing.companyName')}{' '}
                    <span className="text-red-500">*</span>
                  </span>
                )}
                <input
                  id="billing_nom_m"
                  className={inp}
                  value={draft.billing_nom}
                  aria-invalid={!!formErrors.billing_nom}
                  onChange={(e) => {
                    setField('billing_nom', e.target.value)
                    setField('client_name', e.target.value)
                  }}
                  placeholder={t('transferts.reservations.billing.companyPlaceholder')}
                />
                <FieldErr tamarra={tamarra}>{formErrors.billing_nom}</FieldErr>
              </label>

              <label className={pf || undefined} htmlFor="billing_matricule_fiscal">
                {tamarra ? (
                  <span>{t('transferts.reservations.billing.taxId')}</span>
                ) : (
                  <span className={labelClass}>
                    {t('transferts.reservations.billing.taxId')}{' '}
                    <span className="text-red-500">*</span>
                  </span>
                )}
                <input
                  id="billing_matricule_fiscal"
                  className={inp}
                  value={draft.billing_matricule_fiscal}
                  aria-invalid={!!formErrors.billing_matricule_fiscal}
                  onChange={(e) => {
                    setField('billing_matricule_fiscal', e.target.value)
                  }}
                />
                <FieldErr tamarra={tamarra}>{formErrors.billing_matricule_fiscal}</FieldErr>
              </label>

              <div className={tamarra ? 'tamarra-billing-phone' : undefined}>
                <CountryPhoneInput
                  id="billing_telephone_m"
                  countryCode={draft.phone_country_code || '+216'}
                  phone={draft.billing_telephone}
                  onCountryCodeChange={(v) => setField('phone_country_code', v)}
                  onPhoneChange={(v) => {
                    setField('billing_telephone', v)
                    setField('client_phone', v)
                  }}
                />
              </div>

              <label className={pf || undefined} htmlFor="billing_email_m">
                {tamarra ? (
                  <span>{t('transferts.common.email')}</span>
                ) : (
                  <span className={labelClass}>{t('transferts.common.email')}</span>
                )}
                <input
                  id="billing_email_m"
                  type="email"
                  className={inp}
                  value={draft.billing_email}
                  onChange={(e) => {
                    setField('billing_email', e.target.value)
                    setField('client_email', e.target.value)
                  }}
                />
              </label>

              <label
                className={tamarra ? `${pf} tamarra-billing-wide` : 'md:col-span-2'}
                htmlFor="billing_adresse_facturation_m"
              >
                {tamarra ? (
                  <span>{t('transferts.reservations.billing.billingAddress')}</span>
                ) : (
                  <span className={labelClass}>
                    {t('transferts.reservations.billing.billingAddress')}{' '}
                    <span className="text-red-500">*</span>
                  </span>
                )}
                {tamarra ? (
                  <input
                    id="billing_adresse_facturation_m"
                    type="text"
                    className={inp}
                    value={draft.billing_adresse_facturation}
                    aria-invalid={!!formErrors.billing_adresse_facturation}
                    onChange={(e) => {
                      setField('billing_adresse_facturation', e.target.value)
                    }}
                  />
                ) : (
                  <textarea
                    id="billing_adresse_facturation_m"
                    className={area}
                    rows={3}
                    value={draft.billing_adresse_facturation}
                    aria-invalid={!!formErrors.billing_adresse_facturation}
                    onChange={(e) => {
                      setField('billing_adresse_facturation', e.target.value)
                    }}
                  />
                )}
                <FieldErr tamarra={tamarra}>{formErrors.billing_adresse_facturation}</FieldErr>
              </label>
            </div>
          )}

          {canCreateClient ? (
            <div
              className={
                tamarra
                  ? 'tamarra-banner'
                  : 'rounded-[var(--radius-md)] border border-dashed border-sky-200 bg-sky-50/60 p-3'
              }
            >
              <p className={tamarra ? undefined : 'text-xs text-sky-900'}>
                {t('transferts.reservations.billing.notFoundCta')}
              </p>
              <button
                type="button"
                className={
                  tamarra
                    ? 'tamarra-btn-ghost'
                    : 'mt-2 inline-flex min-h-[44px] items-center justify-center rounded-[var(--radius-md)] bg-sky-700 px-4 py-2 text-sm font-medium text-[var(--primary-foreground)] transition hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-60'
                }
                onClick={() => handleCreateBillingClient()}
                disabled={isCreatingClient}
                aria-label={t('transferts.reservations.billing.createClientAria')}
              >
                {isCreatingClient
                  ? t('transferts.reservations.billing.creating')
                  : t('transferts.reservations.billing.createClient')}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  )
}
